import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Step-Komponenten
import BasicInfoStep from '../components/chatbot-creation/BasicInfoStep';
import PersonalityStep from '../components/chatbot-creation/PersonalityStep';
import KnowledgeStep from '../components/chatbot-creation/KnowledgeStep';
import AppearanceStep from '../components/chatbot-creation/AppearanceStep';
import ReviewStep from '../components/chatbot-creation/ReviewStep';

const STEPS = [
    { id: 'basic-info', title: 'Grundinformationen' },
    { id: 'personality', title: 'Persönlichkeit' },
    { id: 'knowledge', title: 'Wissensquellen' },
    { id: 'appearance', title: 'Erscheinungsbild' },
    { id: 'review', title: 'Überprüfung' },
];

// Interface für die Chatbot-Daten
interface ChatbotData {
    name: string;
    description: string;
    primaryUseCase: string;
    personality: {
        tone: string;
        formality: string;
        responseLength: string;
    };
    knowledge: string[];
    uploadedFiles: File[];
    appearance: {
        theme: string;
        primaryColor: string;
        position: string;
    };
}

const CreateChatbot: React.FC = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);

    // Initialer Zustand für den Chatbot
    const [chatbotData, setChatbotData] = useState<ChatbotData>({
        name: '',
        description: '',
        primaryUseCase: 'customer-service',
        personality: {
            tone: 'friendly',
            formality: 'casual',
            responseLength: 'concise',
        },
        knowledge: [],
        uploadedFiles: [],
        appearance: {
            theme: 'light',
            primaryColor: '#3b82f6',
            position: 'right',
        },
    });

    // Handler zum Aktualisieren der Chatbot-Daten
    const updateData = (data: Partial<ChatbotData>) => {
        setChatbotData(prev => ({
            ...prev,
            ...data,
        }));
    };

    // Zur nächsten Stufe gehen
    const nextStep = () => {
        if (currentStep < STEPS.length - 1) {
            setCurrentStep(prev => prev + 1);
            window.scrollTo(0, 0);
        }
    };

    // Zur vorherigen Stufe gehen
    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
            window.scrollTo(0, 0);
        }
    };

    // Chatbot erstellen und zur Einstellungsseite navigieren
    const createChatbot = () => {
        // Hier würde ein API-Aufruf zum Erstellen des Chatbots erfolgen
        console.log('Chatbot wird erstellt:', chatbotData);

        // Mock-Implementation: Wir simulieren eine erfolgreiche Erstellung und navigieren zur Einstellungsseite
        setTimeout(() => {
            navigate('/chatbot/new-chatbot-id');
        }, 1000);
    };

    // Komponenten für jeden Schritt im Erstellungsprozess
    const renderStepComponent = () => {
        switch (currentStep) {
            case 0: // Basic Info
                return <BasicInfoStep data={chatbotData} updateData={updateData} />;
            case 1: // Personality
                return <PersonalityStep data={chatbotData} updateData={updateData} />;
            case 2: // Knowledge
                return <KnowledgeStep data={chatbotData} updateData={updateData} />;
            case 3: // Appearance
                return <AppearanceStep data={chatbotData} updateData={updateData} />;
            case 4: // Review
                return <ReviewStep data={chatbotData} />;
            default:
                return <BasicInfoStep data={chatbotData} updateData={updateData} />;
        }
    };

    return (
        <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-6">Neuen Chatbot erstellen</h1>

            {/* Progress Steps */}
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    {STEPS.map((step, index) => (
                        <div key={step.id} className="flex flex-col items-center">
                            <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                                index < currentStep
                                    ? 'bg-blue-500 border-blue-500 text-white'
                                    : index === currentStep
                                        ? 'border-blue-500 text-blue-500'
                                        : 'border-gray-300 text-gray-300'
                            }`}>
                                {index < currentStep ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : (
                                    <span>{index + 1}</span>
                                )}
                            </div>
                            <span className={`mt-2 text-xs font-medium ${
                                index <= currentStep ? 'text-blue-500' : 'text-gray-500'
                            }`}>
                {step.title}
              </span>
                        </div>
                    ))}
                </div>
                <div className="mt-4 hidden sm:flex">
                    <div className="w-full bg-gray-200 rounded-full h-1">
                        <div
                            className="bg-blue-500 h-1 rounded-full transition-all duration-300 ease-in-out"
                            style={{ width: `${(currentStep / (STEPS.length - 1)) * 100}%` }}
                        ></div>
                    </div>
                </div>
            </div>

            {/* Current Step Content */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                {renderStepComponent()}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between">
                <button
                    type="button"
                    onClick={prevStep}
                    className={`px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 ${
                        currentStep === 0 ? 'invisible' : ''
                    }`}
                >
                    Zurück
                </button>

                <div>
                    {currentStep < STEPS.length - 1 ? (
                        <button
                            type="button"
                            onClick={nextStep}
                            className="ml-3 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Weiter
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={createChatbot}
                            className="ml-3 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-500 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                            Chatbot erstellen
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreateChatbot;